import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FeeRule } from '@/api/notices';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// CategoryFeeRow is the compact inline editor for one category fee. Every fee
// has a free-text NAME (what shows on the notice) plus a billing type:
// "recurring" (the training fee — charged every period in a monthly plan) or
// "one-time" (an add-on like equipment/registration — charged once). It reports
// the raw values; the parent (CategoryFeesTab) assembles the fee_rule payload.

// Only the everyday periods — the full engine enum (season/custom) carries
// dates this simplified surface can't set, so it's kept out.
const PERIODS = ['spring', 'autumn', 'full_year'];
const ANY = '__any__';

// 'recurring' → the training fee (recurs in monthly plans); 'one_time' → an
// add-on (billed once). The kind maps to training_minor vs addon_minor.
export type CategoryFeeKind = 'recurring' | 'one_time';

export interface CategoryFeeValues {
  kind: CategoryFeeKind;
  label: string;
  amountMinor: number;
  minAge: number | null;
  maxAge: number | null;
  period: string | null;
}

interface Props {
  rule: FeeRule | null; // null = new
  saving: boolean;
  onCancel: () => void;
  onSave: (values: CategoryFeeValues) => void;
}

function toIntOrNull(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function initialKind(rule: FeeRule | null): CategoryFeeKind {
  return rule?.addon_minor != null ? 'one_time' : 'recurring';
}
function initialAmount(rule: FeeRule | null): string {
  const minor = rule?.addon_minor ?? rule?.training_minor;
  return minor != null ? String(minor / 100) : '';
}

export function CategoryFeeRow({ rule, saving, onCancel, onSave }: Props) {
  const { t } = useTranslation('common');

  const [kind, setKind] = useState<CategoryFeeKind>(initialKind(rule));
  const [label, setLabel] = useState(rule?.label ?? '');
  const [amount, setAmount] = useState(initialAmount(rule));
  const [minAge, setMinAge] = useState(rule?.min_age != null ? String(rule.min_age) : '');
  const [maxAge, setMaxAge] = useState(rule?.max_age != null ? String(rule.max_age) : '');
  const [period, setPeriod] = useState(rule?.period ?? '');

  const amountNum = Number(amount.trim());
  const amountValid = amount.trim() !== '' && Number.isFinite(amountNum) && amountNum >= 0;
  const valid = amountValid && label.trim() !== '';

  function submit() {
    if (!valid) return;
    onSave({
      kind,
      label: label.trim(),
      amountMinor: Math.round(amountNum * 100),
      minAge: toIntOrNull(minAge),
      maxAge: toIntOrNull(maxAge),
      period: period || null,
    });
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-lime bg-muted/20 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('notices.fees.feeName', { defaultValue: 'Fee name' })}
          </span>
          <Input
            value={label}
            autoFocus
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('notices.fees.feeNamePlaceholder', { defaultValue: 'e.g. Training fee' })}
            className="rounded-pill"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('notices.fees.feeType', { defaultValue: 'Fee type' })}
          </span>
          <Select value={kind} onValueChange={(v) => setKind(v as CategoryFeeKind)}>
            <SelectTrigger className="min-h-[var(--tap)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recurring">
                {t('notices.fees.kind.recurring', { defaultValue: 'Recurring (training fee)' })}
              </SelectItem>
              <SelectItem value="one_time">
                {t('notices.fees.kind.oneTime', { defaultValue: 'One-time fee' })}
              </SelectItem>
            </SelectContent>
          </Select>
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('notices.fees.kindHelp', {
          defaultValue:
            'Recurring fees are charged each period in a monthly plan; one-time fees are charged once.',
        })}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('notices.fees.amount', { defaultValue: 'Amount (kr)' })}
          </span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="rounded-pill"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('notices.fees.minAgeOptional', { defaultValue: 'Min age (optional)' })}
          </span>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={minAge}
            onChange={(e) => setMinAge(e.target.value)}
            className="rounded-pill"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('notices.fees.maxAgeOptional', { defaultValue: 'Max age (optional)' })}
          </span>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            value={maxAge}
            onChange={(e) => setMaxAge(e.target.value)}
            className="rounded-pill"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">
          {t('notices.fees.field.period', { defaultValue: 'Period' })}
        </span>
        <Select value={period || ANY} onValueChange={(v) => setPeriod(v === ANY ? '' : v)}>
          <SelectTrigger className="min-h-[var(--tap)]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>
              {t('notices.fees.anyPeriod', { defaultValue: 'Any period' })}
            </SelectItem>
            {PERIODS.map((p) => (
              <SelectItem key={p} value={p}>
                {t(`notices.period.${p}`, { defaultValue: p })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={submit} disabled={!valid || saving}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  );
}
